import { useSetAtom } from 'jotai';
import { ChangeEvent, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import CloseIcon from '@flipster/ui/assets/svgs/close.svg';
import SearchIcon from '@flipster/ui/assets/svgs/search.svg';
import { getFlipsterConfigsV1Key, getLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { MarketEvent } from '@flipster/utils/segment/events/market';

import { RecentSearchHistory } from '../../market-discover/recent-search-history';

interface Props {
  value: string;
  placeholder: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onClear?: () => void;
}

export const SearchInput = ({ value, placeholder, onChange, onFocus, onClear }: Props) => {
  const [isShowSearchHistory, setIsShowSearchHistory] = useState(false);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);
  const searchInputRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();

  const handleOnFocus = () => {
    onFocus?.();
    setIsShowSearchHistory(true);

    const key = user ? getFlipsterConfigsV1Key(user.userId, 'recentSearchHistory') : null;
    segment.track(MarketEvent.SearchBarClick, { is_history: !!key && !!getLocalStorage(key) });
  };

  useClickAway(searchInputRef, () => setIsShowSearchHistory(false));

  return (
    <div
      className={hstack({
        position: 'relative',
        gap: 12,
        width: { base: '100%', tablet: 387 },
        height: 46,
        padding: '12px 16px',
        backgroundColor: 'background.default.level-1',
        borderRadius: 8,
      })}
      ref={searchInputRef}
    >
      <SearchIcon width={20} height={20} color={token('colors.inverse.white')} />
      <input
        type='text'
        value={value}
        onChange={onChange}
        size={10}
        onFocus={handleOnFocus}
        placeholder={placeholder}
        className={css({
          flex: 1,
          color: 'inverse.white',
          caretColor: 'blue.500',
          _placeholder: {
            color: 'content.interaction.disabled',
          },
        })}
      />
      {value ? (
        <button
          type='button'
          className={css({
            color: 'inverse.black',
            backgroundColor: 'content.default.level-1',
            borderRadius: '50%',
          })}
          onClick={onClear}
        >
          <CloseIcon width={16} height={16} className={flex()} />
        </button>
      ) : undefined}
      {!value && isShowSearchHistory && (
        <RecentSearchHistory
          onClickItem={() => setOrderAttribution(OrderAttributionMapper.market.searchRecentHistory)}
          className={css({
            position: 'absolute',
            left: 0,
            right: 0,
            top: 56,
            p: 16,
            borderRadius: '8px',
            backgroundColor: 'background.default.level-1',
          })}
        />
      )}
    </div>
  );
};
